define([
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/config",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/resolver",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/view",
	"csi/xml",
	"Apparel/ProductType",
	"./MaterialFreezeDefaultQuote",
	"./NewBreadcrumbSupplierRequestAction",
	"./StyleFreezeProductionQuote"
], function(csi,_, crews, config, reflection, reflectionHints, resolver, sessionConstants, { sessionStore, routerStore }, view, xml, ProductType) {

	return {
		getLibraryNode: function() {
			// summary: return library node
			return resolver.resolveNode("centric://REFLECTION/INSTANCE/SiteLibSupplierItem/SiteLibSupplierItem", true);
		},

		isCurrentUserAgentUser: function() {
			return sessionStore.isAgentUser;
		},

		/** verify the existence of the parent or the view node */
		onAccessActionVerifyNode: function(ctx, action) {
			return !!(ctx.getNodeParent() || ctx.view.getViewNode());
		},

		onAccessActionCopySupplierRequestQuote: function(ctx, action) {
			const roles = sessionConstants.isSupplierUser ? ["Quote Supplier Admin"] : ["Sourcing Admin", "Sourcing Editor"];
			return crews.isInRoles(roles, ctx.getNodeParent());
		},

		onAccessActionSRLineItemProductColors: function(ctx, action) {
			return !!ctx.defView.findHeading('ProductColors', 'Child:SRLineItemProduct');
		},

		onAccessActionSupplierItemColors: function(ctx, action) {
			return !!ctx.defView.findHeading('Colors', 'Child:CurrentRevision');
		},

		onAccessActionOfflineCostingPermission: function(/*Context*/ctx, /*Action*/action) {
			if (!this.onAccessActionVerifyNode(ctx, action)) {
				return false;
			}
			return crews.isInRoles(["Sourcing Offline Administrator"], ctx.getNodeParent() || ctx.view.getViewNode()); // DE28266
		},

		onAccessActionMassActionSupplierItems: function(/*Context*/ctx, /*Action*/action) {
			if (!this.onAccessActionVerifyNode(ctx, action)) {
				return false;
			}
			return crews.isInRoles(["Sourcing Admin"], ctx.getNodeParent() || ctx.view.getViewNode());
		},

		onAccessActionLimitExportSize: function(/*Context*/ctx, /*Action*/action) {
			// respect max export size
			const store = ctx.view.get("store");

			if (!store) {
				return false;
			}

			const totalRows = store.getTotal();
			const maxExportSize = sessionConstants.siteMaxExportSize;
			return (totalRows <= maxExportSize);
		},

		onAccessActionRequireCustomView: function(/*Context*/ctx, /*Action*/action) {
			// require a custom view before export is allowed
			return xml.isValidURL(ctx.view.get("customViewId"));
		},

		onAccessActionExportRules: function(/*Context*/ctx, /*Action*/action) {
			return this.onAccessActionLimitExportSize(ctx, action) && this.onAccessActionRequireCustomView(ctx, action) && this.onAccessActionOfflineCostingPermission(ctx, action);
		},

		onAccessActionProductNewFromTheme: function(/*Context*/ctx, /*Action*/action) {
			// TA26281: Display the Style PA action "New from Theme" only if the style is in a Theme and the Theme has > 0 active Theme Product Alternatives
			var productNode = ctx.getParent();
			if (!productNode || !xml.isValidURL(productNode.Theme))
				return false;

			var theme = ctx.cache.getPathNode(productNode, "Child:Theme", true);
			var themePAs = ctx.cache.getChildren(theme, "ThemeProductAlternatives", true);
			if (!themePAs)
				return false;

			return _.some(themePAs, function(themePA) {
				return themePA && themePA.Active;
			});
		},

		onAccessActionSetAsStyleProductionQuote: function(/*Context*/ctx, /*Action*/action) {
			//Display 'Set as Production Source' action ONLY when the parent (StyleSourcing) Production Source is not frozen
			var productSource = ctx.getNodeParent();
			var styleSourcingNode = ctx.cache.getNode(productSource.__Parent__, true);
			return !xml.nodeAttributeHasFlag(styleSourcingNode, "ProductionQuote", xml.ATTR_FLAG_NOEDIT);
		},

		onAccessActionSetAsMaterialDefaultQuote: function(/*Context*/ctx, /*Action*/action) {
			//Display 'Set as Production Source' action ONLY when the parent (MaterialSourcing) Production Source is not frozen
			var productSource = ctx.getNodeParent();
			var materialSourcingNode = ctx.cache.getNode(productSource.__Parent__, true);
			return !xml.nodeAttributeHasFlag(materialSourcingNode, "DefaultQuote", xml.ATTR_FLAG_NOEDIT);
		},

		onAccessActionCopyLineItemSample: function(/*Context*/ctx, /*Action*/action) {
			var productSourceNode = ctx.getNodeParent();
			return xml.nodeHasRight(productSourceNode, xml.RIGHT_WRITE);
		},

		onAccessActionCreateSupplierMaterial: function(/*Context*/ctx, /*Action*/action) {
			var supplierMSGs = ctx.cache.getNodes(ctx.view.getViewNode().SupplierMSGs);

			// make sure there is permission for at least one supplierMSG
			return _.some(supplierMSGs, function(supplierMSG) {
				return crews.isInRoles(["Material Editor", "Material Administrator"], supplierMSG);
			});
		},

		onAccessActionNewSupplierRequest: function(/*Context*/ctx, /*Action*/action) {
			return crews.isInRoles(["Supplier Request Administrator"], ctx.getNode()) ||
				crews.isInRoles(["Supplier Request Administrator"], ctx.view.getViewNode());
		},

		onAccessActionNewSupplierRequestMaster: function(/*Context*/ctx, /*Action*/action) {
			return crews.isInRoles(["Supplier Request Master Administrator"], ctx.getNode()) || 
				crews.isInRoles(["Supplier Request Master Administrator"], ctx.view.getViewNode());
		},

		/**
		 * C8-41118: Disable breadcrumb SR Copy action for F&B Types.
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionCopySupplierRequest: function(ctx, action) {
			const requestType = xml.enumGetText(ctx.getNode().RequestType);
			if (["Material", "Style"].includes(requestType)) {
				return crews.isInRoles(["Supplier Request Administrator"], ctx.getNode()) ||
					crews.isInRoles(["Supplier Request Administrator"], ctx.view.getViewNode());
			} else {
				return false;
			}
		},

		onAccessActionNotTemplateNode: function(ctx, action) {
			const node = ctx.cache.getNode(ctx.getNode().__Parent__);
			return !reflectionHints.isTemplateNode(node);
		},

		/**
		 * Show Delete SRM action only if all SRs in SRM are in Draft or Complete state and if the user has appropriate roles based on SRMSupplierRequests
		 * @param {Context} ctx 
		 * @param {Action} action 
		 */
		onAccessActionDeleteSRM(ctx, action) {
			const currentSRM = ctx.getNode();
			const srUrlList = currentSRM.SRMSupplierRequests;
			if (srUrlList.length) {
				const allStatesDraftOrComplete = srUrlList.map((srUrl) => ctx.cache.getNode(srUrl)?.State)
													.every((state) => (state === "SupplierRequestState:Draft" || state ==="SupplierRequestState:Complete"));
				return allStatesDraftOrComplete && crews.isInRoles(["Supplier Request Master Administrator"], currentSRM);
			}
			return crews.isInRoles(["Supplier Request Master Administrator"], currentSRM);
		},

		/**
		 * Hide ManageColorwayAllocation on Product Source table row if the Source not has the right
		 * @param {Context} ctx 
		 * @param {Action} action 
		 * @returns 
		 */
		onAccessActionManageColorwayAllocation(ctx, action) {
			const ps = ctx.getNode();
			const sourceNode = ctx.cache.getNode(ps.SupplierFactory) || ctx.cache.getNode(ps.Supplier);
			return xml.nodeHasRight(sourceNode, xml.RIGHT_READ);
		},

		onAccessActionCreateSharedDataSheetsforAgents(ctx, action) {
			const roles = ["TDS - BOM Administrator", "Product Specification Administrator", "TDS - BOM Creator"];
			return crews.isInRoles(roles, ctx.getNode()) || crews.isInRoles(roles, ctx.view.getViewNode());
		},
			
			

		/**
		 * Ensure that the Selected column in the table of quotes is updated when the ProductionQuote is changed
		 * @param {Controller} ac
		 */
		postActionSelectQuote(ac) {
			ac.widget.getSiblingView("SupplierItems")?.redrawData();
		},

		postActionSelectDPS: function(ac) {
			var v = view.getView("QuoteProperties");
			if (v) {
				v.refreshWidget();
			}
		},

		postActionRefreshSR: function(ac) {
			const generalQuoteTab = ac.widget.viewParent.getSiblingView("SRQuotes");
			
			if (generalQuoteTab) {
				generalQuoteTab.refreshWidget();
			}
		},


		/**

		 * Create SupplierItems according to form.
		 * @param {Writer} writer
		 * @param {FormData} formData
		 * @param {Function} creator
		 * @param {URL} supplierURL
		 * @param {string} supplierName
		 * @return {Array<URL>} - URL's of the newly created SupplierItems
		 */

		createSupplierItems: function(writer, formData, creator, supplierURL, supplierName) {
			// FormData must have 'colorSizePairs' object in the format {c:<color-node>, s:<size-node>}.
			// 'colorSizePairs' can be null if the user choose do not create color/size specific SupplierItem-s
			var colorSizePairs = formData.colorSizePairs;
			var urlNewItems;

			if (!colorSizePairs || colorSizePairs.length == 0) {
				colorSizePairs = [{c: null, s: null}];
			}

			urlNewItems = _.map(colorSizePairs, function(pair) {
				formData.clearUsedFields();

				const names = [supplierName];
				const nameField = formData.getField(xml.ATTR_NAME);
				let name = formData.getFieldValue(xml.ATTR_NAME, true);
				if (name) {
					names.push(name);
				}

				const color = pair.c;
				const size = pair.s;
				const urlNode = creator(supplierURL);
				const urlRevision = xml.getUrlPath(urlNode, reflection.PATH_CURRENT_REVISION);
				const cn = writer.changeNode(urlRevision);

				if (color) {
					names.push(color.$Name);
					cn.changeAttribute("Colors", xml.ATTR_REFLIST, [color.$URL]);
				}

				if (size) {
					names.push(size.$Name);
					cn.changeAttribute("ProductSizes", xml.ATTR_REFLIST, [size.$URL]);
				}

				if (nameField && (!nameField.hasExpression() || !nameField.isDefaultValue())) {
					writer.changeNode(urlNode)
						.changeAttributeFlag(xml.ATTR_NAME, xml.ATTR_FLAG_EVAL_EXPRESSION | xml.ATTR_FLAG_ONE_TIME_EXPRESSION, false, true)
						.changeAttributeExp(xml.ATTR_NAME, "")
						.changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, names.join("-"));
				}
				writer.validateNode(urlNode, true);
				return urlNode;
			});

			return urlNewItems;

		},

		/**
		 * Check if Supplier Request Subtype is sourced from Supplier
		 * @param {String} subtypeUrl
		 * @returns {boolean}
		 */
		isSubtypeSupplierSourced(subtypeUrl) {
			const sourceCreationLevel = config.getSubtypeSetting(subtypeUrl, "ProductSourceCreationLevel");
			return sourceCreationLevel === "ProductSourceCreationLevel:Supplier";
		},

		/**
		 * Check if Supplier Request Subtype is Allocated
		 * @param {String} subtypeUrl
		 * @returns {boolean}
		 */
		isSubtypeAllocated(subtypeUrl) {
			return config.getSubtypeSetting(subtypeUrl, "SourcingType") === "SourcingType:Allocated";
		},

		/**
		 * Check if Product has Colorway Allocation Status == WIP
		 * @param {C8Node} productNode 
		 * @returns {boolean}
		 */
		isProductColorwayAllocationWIP(productNode) {
			return productNode?.ColorAllocationStatus === "ColorAllocationStatus:Allocation WIP";
		},

		/**
		 * @param {cnl} nodeUrl
		 * @cache {Cache} cache
		 * @returns {boolean} the ProductSourceCreationLevel for the current BaseProduct or SupplierRequest instance
		 */
		getProductSourceCreationLevel(nodeUrl, cache) {
			if(xml.isValidURL(nodeUrl)) {
				const source = cache.getNode(nodeUrl);
				const rc = reflection.getClass(source.$Type);
				if(rc.isDerivedFrom("Product")) {
					return ProductType.get(source, "ProductSourceCreationLevel");
				} else if(rc.isDerivedFrom("FormulatedProduct") || rc.isDerivedFrom("SupplierRequest")) {
					return config.getSubtypeSetting(source.Subtype, "ProductSourceCreationLevel");
				} else if(rc.isDerivedFrom("SupplierRequestMaster")) {
					return config.getSubtypeSetting(source.SRSubtype, "ProductSourceCreationLevel");
				} else if(rc.isDerivedFrom("SupplierRequestSubtype")) {
					return config.getSubtypeSetting(nodeUrl, "ProductSourceCreationLevel");
				}
			}
			return null;
		},

		/**
		 * @param {C8Node} node
		 * @cache {Cache} cache
		 * @returns {boolean} true if the ProductSourceCreationLevel for the current BaseProduct, BaseProductSourcing or SupplierRequest instance is Factory
		 */
		isFDDEnabled: function(node, cache) {
			let creationLevel;
			if(node) {
				const rc = reflection.getClass(node.$Type);
				if(node.$Type === "SupplierRequest" || node.$Type === "SupplierRequestSubtype" || rc.isDerivedFrom("BaseProduct")) {
					creationLevel = this.getProductSourceCreationLevel(node.$URL, cache);
				} else if(rc.isDerivedFrom("BaseProductSourcing")) {
					creationLevel = this.getProductSourceCreationLevel(node.__Parent__, cache);
				}

			}
			return creationLevel === "ProductSourceCreationLevel:Factory";
		},

		/**
		 * @param {String} subtypeValue
		 * @returns {boolean} true if subtype -> sourcingtype is default
		 */
		filterDPTFalseSupplierSpecific(subtypeValue) {
			const sourcingtype = config.getSubtypeSetting(subtypeValue, "SourcingType");
			return sourcingtype === "SourcingType:Default";
		},

		/**
		 * @param {String} subtypeValue
		 * @returns {boolean} true if subtype  -> sourcingtype is supplier specific
		 */
		filterDPTTrueSupplierSpecific(subtypeValue) {
			const sourcingtype = config.getSubtypeSetting(subtypeValue, "SourcingType");
			return sourcingtype === "SourcingType:SupplierSpecific";
		},

		/**
		 * if Product has ColorAllocation == wip and QuotePerActiveColor/SamplePerActiveColor is selected,
		 * 	check if the color exists in the ColorProductSource.Colorway
		 * @return {Boolean}
		 */
		isColorInColorSources(lineItemUrl, colorUrl, srcUrl, isSupplierFactory, cache){
			const lineItemNode = cache.getNode(lineItemUrl);
			// get the ProductSource that have the same supplier as the one selected
			let productSources = lineItemNode.SRLIProductSources;
			productSources = productSources.filter((ps) => {
				const psNode = cache.getNode(ps);
				return isSupplierFactory ? psNode.SupplierFactory === srcUrl : psNode.Supplier === srcUrl;
			});
			const ps = cache.getNode(productSources[0]);
			// Check if the ColorProductSource of the matching ProductSource has the color we want in ColorProductSource.Colorway
			const isColorInColorSources = ps?.ColorSources.some((cps) => cache.getNode(cps)?.Colorway === colorUrl && cache.getNode(cps)?.Active);
			return isColorInColorSources;
		},

		/**
		 * check if the color exists in the ColorProductSource.Colorway without using LineItem
		 * @return {Boolean}
		 */
		isColorInColorSourcesNoLineItem(productNode, colorUrl, srcUrl, isFDDEnabled, cache){
			const sourcing = cache.getNode(productNode.ProductSourcing);
			let productSources = sourcing.ProductSources;
			// get the ProductSource that have the same supplier as the one selected
			productSources = productSources.filter((ps) => {
				const psNode = cache.getNode(ps);
				return isFDDEnabled ? psNode?.SupplierFactory === srcUrl : psNode?.Supplier === srcUrl;
			});
			const ps = cache.getNode(productSources[0]);
			// Check if the ColorProductSource of the matching ProductSource has the color we want in ColorProductSource.Colorway
			const isColorInColorSources = ps?.ColorSources.some((cps) => cache.getNode(cps)?.Colorway === colorUrl && cache.getNode(cps)?.Active);
			return isColorInColorSources;
		},

		/**
		 * Get count of Colors in active CPS for statistics panel for allocation WIP products
		 * @param {C8Node} productNode 
		 * @param {any} supplierOrFactoryValue 
		 * @param {Cache} cacheView 
		 * @returns {Number}
		 */
		getCountColorsInActiveCPS(productNode, supplierOrFactoryValue, cacheView, lineItem) {
			let allocatedCPSColorsLength = 0;
			const isFDDEnabled = this.isFDDEnabled(productNode, cacheView)
			
			// this._colors can be empty if mulitple sources and products are selected so use product colors
			let pc = this._colors?.length ? this._colors.map(c => c.$URL) : (productNode.$Type == "Style" ?
																			productNode.ActiveColorways :
																			cacheView.getNodes(productNode.ProductColors).filter(n => n.Active).map(n => n.$URL));
			if (Array.isArray(supplierOrFactoryValue)) {
				// if supplier or factory is multi select then go through each of them and get the colors length in active CPS
				if (pc.length){
					supplierOrFactoryValue.forEach((e) => {
						allocatedCPSColorsLength += pc?.filter(c => this.isColorInColorSources(lineItem, c, e, isFDDEnabled, cacheView)).length;
					});
				}
			} else {
				// if supplier or factory is single select just use the value of the field and get the colors length in the active CPS
				allocatedCPSColorsLength = this._colors.length;
			}
			return allocatedCPSColorsLength;
		},

		/**
		 * Get count of Colors in active CPS for statistics panel for allocation WIP products
		 * @param {C8Node} productNode 
		 * @param {any} supplierOrFactoryValue 
		 * @param {Cache} cacheView 
		 * @returns {Number}
		 */
		getCountColorsInActiveCPSNoLineItem(productNode, supplierOrFactoryValue, cacheView) {
			let allocatedCPSColorsLength = 0;
			const isFDDEnabled = this.isFDDEnabled(productNode, cacheView);
			
			let pc = productNode.$Type == "Style" ?
						productNode.ActiveColorways :
						cacheView.getNodes(productNode.ProductColors).filter(n => n.Active).map(n => n.$URL);
			if (Array.isArray(supplierOrFactoryValue)) {
				if(pc?.length) {
					// if supplier or factory is multi select then go through each of them and get the colors length in active CPS
					supplierOrFactoryValue.forEach((e) => {
					allocatedCPSColorsLength += pc?.filter(c => this.isColorInColorSourcesNoLineItem(productNode, c, e, isFDDEnabled, cacheView)).length;
					});
				}
			} else {
				// if supplier or factory is single select just use the value of the field and get the colors length in the active CPS
				allocatedCPSColorsLength = pc?.filter(c => this.isColorInColorSourcesNoLineItem(productNode, c, supplierOrFactoryValue, isFDDEnabled, cacheView)).length;
			}
			return allocatedCPSColorsLength;
		},

		/**
		 * Get number of product sources with the given supplier or factory values that have ProductSource.DefaultColor
		 * @param {C8Node} product 
		 * @param {String[]} supplierOrFactoryFieldValue
		 */
		getCountProductSourcesWithDefaultColor(product, supplierOrFactoryFieldValue, cache) {
			let productSourcing = cache.getNode(product.ProductSourcing);
			let productSources =  cache.getNodes(productSourcing.ProductSources);
			const isFDDEnabled = this.isFDDEnabled(product, cache);
			return productSources.filter(ps => {
				if (!isFDDEnabled) {
					return supplierOrFactoryFieldValue.includes(ps.Supplier) && ps.DefaultColor !== csi.EMPTY_URL;
				}
				return supplierOrFactoryFieldValue.includes(ps.SupplierFactory) && ps.DefaultColor !== csi.EMPTY_URL;
				}).length;
		},

		/**
		 * Get Active Colors in active ColorProductSources in a ProductSource of a product
		 * @param {C8Node} product 
		 * @param {cnl} source 
		 * @param {Cache} cache 
		 * @returns List of Colorways from active ColorProductSources
		 */
		getActiveColorsInActiveColorProductSources(product, source, cache) {
			const isFDDEnabled = this.isFDDEnabled(product, cache);
			let productSourcing = cache.getNode(product.ProductSourcing);
			let productSource =  cache.getNodes(productSourcing?.ProductSources).filter(ps => {
				if (!isFDDEnabled) {
					return ps.Active && source === ps.Supplier;
				}
				return ps.Active && source === ps.SupplierFactory;
			})[0];
			return cache.getNodes(productSource?.ColorSources).filter((cs) => cs.Active).map((cs) => cs.Colorway);

		}
	};
});
